import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, X, ShieldCheck } from "lucide-react";
import CommandCenter from "@/pages/CommandCenter";
import { BRAND } from "@/config/brand";
import {
  trackPreviewView,
  trackPreviewScrollDepth,
  trackPreviewCtaClick,
} from "@/lib/analytics";

const C = BRAND.colors;

// ─────────────────────────────────────────────────────────────────────
// Client Portal Preview — public peek at what a real client sees after
// login. Renders the full CommandCenter dashboard under a soft blur so
// visitors get the LAYOUT + FIREPOWER without seeing readable numbers,
// with a persistent "book yours" ribbon + floating CTA to convert.
// Route: /preview
// ─────────────────────────────────────────────────────────────────────

export default function ClientPortalPreview() {
  useEffect(() => {
    document.title = "Peek Inside a Live Client Dashboard | PlugitAI";
    const setMeta = (n, c) => {
      let el = document.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement("meta"); el.name = n; document.head.appendChild(el); }
      el.content = c;
    };
    setMeta(
      "description",
      "Get a real look at the PlugitAI client command center — leads, appointments, AI agent activity, payments, pipeline, and team, all in one dashboard. Book yours from $299.99."
    );
    // Preview is a marketing wrapper around /command-center — set noindex
    // + canonical so Google doesn't flag it as duplicate content and keeps
    // ranking equity concentrated on the canonical dashboard demo.
    setMeta("robots", "noindex,follow");
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    let canon = document.querySelector("link[rel='canonical']");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `${origin}/command-center`;

    // ── GA4 / PostHog beacons ────────────────────────────────────────
    // Fire preview_view immediately (referrer captured server-side by GA).
    const referrer = (typeof document !== "undefined" && document.referrer) || "";
    const source = referrer.includes("plugitai") ? "internal" : (referrer ? "external" : "direct");
    trackPreviewView(source);

    // Scroll-depth firing: each threshold fires once so Kit can see the
    // furthest a visitor got. 25 / 50 / 75 / 100 % of scrollable height.
    const fired = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - doc.clientHeight);
      const pct = Math.min(100, Math.round((window.scrollY / max) * 100));
      [25, 50, 75, 100].forEach((mark) => {
        if (pct >= mark && !fired.has(mark)) {
          fired.add(mark);
          trackPreviewScrollDepth(mark);
        }
      });
    };
    // rAF-throttle the listener so noisy trackpads don't hammer GA.
    let ticking = false;
    const throttled = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => { onScroll(); ticking = false; });
    };
    window.addEventListener("scroll", throttled, { passive: true });
    // Fire once on mount in case the viewport already covers >25% of a
    // short dashboard.
    onScroll();

    return () => {
      window.removeEventListener("scroll", throttled);
      setMeta("robots", "index,follow,max-image-preview:large,max-snippet:-1");
    };
  }, []);

  return (
    <div
      className="relative min-h-screen"
      style={{ background: C.ink }}
      data-testid="client-portal-preview"
    >
      {/* ── TOP RIBBON — persistent "you're looking at a live client dashboard" strip.
             Uses the new violet + amber palette to feel on-brand. */}
      <div
        className="fixed top-0 inset-x-0 z-[60] border-b backdrop-blur-md"
        style={{
          background: `linear-gradient(90deg, rgba(139,92,246,0.28) 0%, rgba(10,15,30,0.92) 40%, rgba(252,211,77,0.22) 100%)`,
          borderColor: "rgba(252,211,77,0.35)",
        }}
        data-testid="preview-ribbon"
      >
        <div className="mx-auto max-w-[1600px] px-4 md:px-8 h-11 md:h-12 flex items-center gap-3 md:gap-5 text-[11px] md:text-xs font-black uppercase tracking-[0.16em]">
          <span className="inline-flex items-center gap-1.5 shrink-0" style={{ color: C.amber }}>
            <Lock size={12} /> Live client dashboard
          </span>
          <span className="hidden md:inline-block h-4 w-px" style={{ background: "rgba(255,255,255,0.18)" }} />
          <span className="hidden md:inline-flex items-center gap-1.5 text-white/85">
            <ShieldCheck size={12} style={{ color: C.violetSoft }} /> Data blurred for privacy
          </span>
          <span className="ml-auto flex items-center gap-3">
            <Link
              to="/"
              onClick={() => trackPreviewCtaClick("close")}
              className="hidden sm:inline-flex items-center gap-1.5 text-white/70 hover:text-white transition-colors"
              data-testid="preview-close"
            >
              <X size={13} /> Close preview
            </Link>
            <a
              href="/#pricing"
              onClick={() => trackPreviewCtaClick("ribbon_book")}
              className="inline-flex items-center gap-1.5 px-3 md:px-4 h-8 rounded-full text-white shrink-0 transition-transform hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${C.violet} 0%, ${C.amber} 100%)`,
                boxShadow: `0 10px 24px -8px ${C.violet}88`,
              }}
              data-testid="preview-book-cta"
            >
              Book yours · $299.99 <ArrowRight size={12} />
            </a>
          </span>
        </div>
      </div>

      {/* ── BLURRED DASHBOARD LAYER — real CommandCenter under a soft filter.
             pointer-events-none on wrapper so visitors can't accidentally
             click through and mess with the demo state; the ribbon + floating
             CTA above/below stay interactive. */}
      <div className="pt-11 md:pt-12" data-testid="preview-dashboard-layer">
        <div
          className="pointer-events-none select-none"
          style={{
            filter: "blur(3.2px) saturate(1.15)",
            WebkitFilter: "blur(3.2px) saturate(1.15)",
          }}
          aria-hidden
        >
          <CommandCenter />
        </div>

        {/* Diagonal watermark stripes so it's obvious this is a preview
            and never confused with a real logged-in dashboard. */}
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-[40] opacity-[0.09]"
          style={{
            backgroundImage: `repeating-linear-gradient(-45deg,
              ${C.amber} 0px, ${C.amber} 1px,
              transparent 1px, transparent 160px,
              ${C.violet} 160px, ${C.violet} 161px,
              transparent 161px, transparent 320px)`,
          }}
        />

        {/* Center "PREVIEW" watermark — big, low-opacity, unmissable */}
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-[41] flex items-center justify-center"
          style={{ opacity: 0.06 }}
        >
          <div
            className="font-black tracking-[0.4em] text-white"
            style={{
              fontSize: "clamp(6rem, 18vw, 20rem)",
              transform: "rotate(-14deg)",
              textShadow: `0 0 40px ${C.violet}`,
            }}
          >
            PREVIEW
          </div>
        </div>
      </div>

      {/* ── FLOATING CONVERSION CARD — bottom-right pill that stays with the
             visitor while they scroll the blurred dashboard. */}
      <div
        className="fixed bottom-5 right-4 md:bottom-8 md:right-8 z-[70] max-w-[340px]"
        data-testid="preview-floating-cta"
      >
        <div
          className="rounded-2xl border p-4 md:p-5 backdrop-blur-xl"
          style={{
            background: `linear-gradient(160deg, rgba(139,92,246,0.28) 0%, rgba(10,15,30,0.92) 55%, rgba(252,211,77,0.20) 100%)`,
            borderColor: `${C.violetSoft}66`,
            boxShadow: `0 30px 80px -20px ${C.violet}66, 0 0 0 1px rgba(255,255,255,0.05)`,
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.28em] font-black mb-2" style={{ color: C.amber }}>
            Like what you see?
          </div>
          <div className="text-white font-black text-base md:text-lg leading-snug">
            This dashboard ships with every PlugitAI build.
          </div>
          <div className="mt-1 text-xs" style={{ color: C.textDim }}>
            One login. Leads, chats, pipeline, payments — all in one glass.
          </div>
          <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <a
              href="/#pricing"
              onClick={() => trackPreviewCtaClick("floating_book")}
              className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-full font-black text-[12px] uppercase tracking-[0.18em] text-white transition-transform hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${C.violet} 0%, ${C.amber} 100%)`,
                boxShadow: `0 12px 30px -10px ${C.amber}88`,
              }}
              data-testid="preview-floating-book"
            >
              Book yours <ArrowRight size={12} />
            </a>
            <a
              href="tel:+12148155966"
              onClick={() => trackPreviewCtaClick("floating_call")}
              className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-full font-black text-[12px] uppercase tracking-[0.18em] border transition-colors hover:bg-white/5"
              style={{ borderColor: `${C.amber}66`, color: C.text }}
              data-testid="preview-floating-call"
            >
              Call Kit
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
